import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Collapse,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StarIcon from '@mui/icons-material/Star';
import { ProviderMatch, CoordinationStatus } from '~/types/coordinate';

const FIT_LABEL_STYLE: Record<string, { bgcolor: string; color: string }> = {
  'Best Fit':      { bgcolor: '#e0f7f4', color: '#006e60' },
  'Strong Option': { bgcolor: '#e5f6fb', color: '#153873' },
  'Backup':        { bgcolor: '#f6f6f6', color: '#668098' },
};

interface Props {
  match: ProviderMatch;
  isSelected: boolean;
  coordinationStatus: CoordinationStatus;
  onSelectAndConfirm: (providerId: string) => void;
  rank: number;
}

const ProviderCard: React.FC<Props> = ({
  match,
  isSelected,
  coordinationStatus,
  onSelectAndConfirm,
  rank,
}) => {
  const [whyExpanded, setWhyExpanded] = useState(rank === 0);
  const { provider, totalScore, fitLabel, scores } = match;

  const isConfirming = isSelected && coordinationStatus === 'confirming';
  const isConfirmed = isSelected && coordinationStatus === 'confirmed';
  const isFailed = isSelected && coordinationStatus === 'confirm_failed';
  const isSentOrLater = ['awaiting_response', 'accepted', 'declined'].includes(coordinationStatus);

  const matchedNeedsCount = match.whyRecommended.length;
  const clinicalFitPct = Math.round(scores.clinicalFit);

  const canSelect =
    coordinationStatus === 'matched' ||
    (coordinationStatus === 'confirm_failed' && !isSelected) ||
    (coordinationStatus === 'confirmed' && !isSelected);

  const cardBorder = isConfirmed
    ? '2px solid'
    : isFailed
    ? '2px solid'
    : isSelected && (isConfirming || isSentOrLater)
    ? '2px solid'
    : '1px solid';

  const cardBorderColor = isConfirmed
    ? '#00bda1'
    : isFailed
    ? '#e65100'
    : isSelected
    ? 'primary.main'
    : 'divider';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        border: cardBorder,
        borderColor: cardBorderColor,
        opacity: isFailed ? 0.55 : 1,
        transition: 'opacity 0.2s, border-color 0.2s',
      }}
    >
      {/* Header row */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              {provider.name}
            </Typography>
            {provider.healthSystemPreferred && (
              <Chip label="Preferred Partner" size="small" variant="outlined"
                sx={{ fontSize: '0.72rem', height: 20, fontWeight: 600, color: '#635f99', borderColor: 'rgba(99,95,153,0.5)' }} />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {provider.distanceMiles} mi away
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={fitLabel}
            size="small"
            sx={{ fontWeight: 700, fontSize: '0.72rem', height: 20, ...(FIT_LABEL_STYLE[fitLabel] ?? {}) }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Score: {totalScore}
          </Typography>
        </Stack>
      </Stack>

      {/* Score badges */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
        <Chip
          icon={<CheckCircleIcon sx={{ color: '#00bda1 !important', fontSize: '0.9rem !important' }} />}
          label="Insurance Match"
          size="small"
          variant="outlined"
          sx={{ color: '#006e60', borderColor: '#00bda1' }}
        />
        <Chip
          label={provider.currentCapacitySignal === 'high' ? 'High Capacity' : provider.currentCapacitySignal === 'medium' ? 'Moderate Capacity' : 'Limited Capacity'}
          size="small"
          variant="outlined"
          sx={
            provider.currentCapacitySignal === 'high'
              ? { color: '#006e60', borderColor: '#00bda1' }
              : provider.currentCapacitySignal === 'medium'
              ? { color: '#153873', borderColor: 'rgba(21,56,115,0.4)' }
              : { color: '#b71c1c', borderColor: 'rgba(183,28,28,0.4)' }
          }
        />
        <Chip
          label={`Clinical Fit ${clinicalFitPct}%`}
          size="small"
          variant="outlined"
          sx={
            clinicalFitPct >= 80
              ? { color: '#006e60', borderColor: '#00bda1' }
              : clinicalFitPct >= 50
              ? { color: '#153873', borderColor: 'rgba(21,56,115,0.4)' }
              : { color: '#668098', borderColor: 'rgba(102,128,152,0.4)' }
          }
        />
        <Chip
          icon={<StarIcon sx={{ fontSize: '0.85rem !important' }} />}
          label={provider.qualityRating.toFixed(1)}
          size="small"
          variant="outlined"
        />
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Action area */}
      {isConfirming && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Confirming availability…</Typography>
        </Stack>
      )}

      {isConfirmed && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CheckCircleIcon sx={{ color: '#00bda1' }} fontSize="small" />
          <Typography variant="body2" sx={{ color: '#006e60' }} fontWeight={600}>
            Confirmed — Ready referral
          </Typography>
        </Stack>
      )}

      {isFailed && (
        <Typography variant="body2" color="warning.main">
          {provider.name} is unavailable — select another provider
        </Typography>
      )}

      {isSentOrLater && isSelected && (
        <Typography variant="body2" color="text.secondary">
          Referral sent to this provider
        </Typography>
      )}

      {canSelect && (
        <Button
          variant={fitLabel === 'Best Fit' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => onSelectAndConfirm(provider.id)}
        >
          Select &amp; Confirm
        </Button>
      )}
    </Paper>
  );
};

export default ProviderCard;
